import React, { Fragment, useState, useEffect, useRef, useCallback } from "react";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormGroup from "@mui/material/FormGroup";
import Paper from "@mui/material/Paper";
import { useTheme } from "@mui/material/styles";
import Alert from "../layout/Alert";
import PushNotification from "./pushNotification";
import UserSelection from "./userSelection";
import {
  notificationChannelTypes,
  notificationConstants,
  pushUserTargetTypes,
  createEmptyPushMessageForm,
  showBroadcastCountryAlert,
  pushNotificationTypeOptions,
  getPushCategoryOptionsForType,
  notificationDetailsRefreshRate,
  notificationStatusHeaders,
} from "./notificationConstant";
import { selectMenuProps } from "../../utils/selectMenuProps";
import AppButton from "../Common/Button/Button";
import AppCheckbox from "../Common/FormInputs/AppCheckbox";
import AppIconButton from "../Common/Button/IconButton";
import { useDesignTokens } from "../../theme/useDesignTokens";
import MuiDatePicker from "../Common/FormInputs/MuiDatePicker";
import FormInput from "../Common/FormInputs/MuiTextField";
import Typography from "@mui/material/Typography";
import DataTable from "../Common/DataTable/DataTable";
import { SectionHeading } from "../Common/Typography/AppTypography";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { format } from "date-fns";
import {
  parseCsvUserIdsFromFile,
  getNotificationExpiryUnixSeconds,
  toNotificationExpiryEndOfDay,
  buildPushMessagePayload,
  validatePushMessageForm,
  getOptionLabel,
} from "./notificationFormUtils";
import {
  emailCompactFormControlSx,
  emailFocusOutlineSx,
  emailSectionCardSx,
} from "./emailFormStyles";
import { INPUT_DESKTOP_MAX_WIDTH } from "../../constants/uiConstants";

const tableHeaders = notificationStatusHeaders;

const lastSentSectionCardSx = {
  p: { xs: 1.5, md: 2 },
  mt: { xs: 1, md: 2 },
  boxShadow: 2,
  borderRadius: 1,
  overflow: "visible",
};

const configGridSx = {
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    sm: "1fr 1fr",
    md: `repeat(4, minmax(0, ${INPUT_DESKTOP_MAX_WIDTH}px))`,
  },
  columnGap: 2,
  rowGap: 2,
  width: "100%",
  alignItems: "start",
  justifyContent: "start",
};

const configFieldSx = {
  display: "flex",
  flexDirection: "column",
  gap: 0.75,
  minWidth: 0,
  width: "100%",
  maxWidth: { xs: "100%", sm: INPUT_DESKTOP_MAX_WIDTH },
};

const configFieldFullSx = {
  ...configFieldSx,
  maxWidth: "100%",
  gridColumn: { xs: "auto", sm: "1 / -1" },
};

const severityOptions = [
  { value: notificationConstants.notifySeverityInfo, label: "Info" },
  { value: notificationConstants.notifySeverityAlert, label: "Alert" },
  {
    value: notificationConstants.notifySeverityRecommendation,
    label: "Recommendation",
  },
  { value: notificationConstants.notifySeverityPromotion, label: "Promotion" },
];

const SendNotification = ({
  allowedCountries,
  setAlert,
  notificationStore: {
    sendNotificationInProgress,
    sendNotificationSuccess,
    lastNotificationDetails,
  },
  createNotificationTask,
  getNotificationTask,
  setDialog,
  setDialogContents,
  setDialogTab,
}) => {
  const theme = useTheme();
  const { colors } = useDesignTokens();
  const primaryColor = colors?.primary?.main || theme.palette.primary.main;
  const focusOutlineSx = emailFocusOutlineSx(primaryColor);
  const controlSx = emailCompactFormControlSx(theme);

  const [expiryDate, setExpiryDate] = useState(undefined);
  const [pnPlatforms, setPnPlatforms] = useState({
    iosPlatform: true,
    androidPlatform: true,
  });
  const [severity, setSeverity] = useState("");
  const [pushMessageData, setPushMessageData] = useState(createEmptyPushMessageForm());

  const [uploadedUserIdFileInput, setUploadedUserIdFileInput] = useState("");
  const userIdCSVFileInputRef = useRef();
  const [country, setCountry] = useState([]);
  const [userTargetType, setUserTargetType] = useState(pushUserTargetTypes.broadcast.value);
  const [notificationTaskData, setNotificationTaskData] = useState(null);
  const [fetchNotificationTimeout, setFetchNotificationTimeout] = useState(null);
  const [copied, setCopied] = useState(false);

  const handlePlatformChange = (platformType, checkedStatus) => {
    setPnPlatforms((prevState) => ({
      ...prevState,
      [platformType]: checkedStatus,
    }));
  };

  const handlePushMessageData = useCallback((field, value) => {
    setPushMessageData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handlePushTypeChange = useCallback((nextType) => {
    setPushMessageData((prev) => ({
      ...prev,
      type: nextType,
      // Reset category when type changes — options are type-specific.
      category: "",
    }));
  }, []);

  const categoryOptions = getPushCategoryOptionsForType(pushMessageData?.type);

  const handleImageUploadError = useCallback(
    (message) => setAlert(message, "danger"),
    [setAlert]
  );

  const handleUserTargetTypeChange = (nextType) => {
    setUserTargetType(nextType);
    if (nextType === pushUserTargetTypes.broadcast.value) {
      setUploadedUserIdFileInput("");
      try {
        if (userIdCSVFileInputRef.current) userIdCSVFileInputRef.current.value = "";
      } catch (_) {
        /* noop */
      }
    } else {
      setCountry([]);
    }
  };

  const handleCreateNotificationTaskReset = useCallback(() => {
    setExpiryDate(undefined);
    setSeverity("");
    setPnPlatforms({ iosPlatform: true, androidPlatform: true });
    setPushMessageData(createEmptyPushMessageForm());
    setUploadedUserIdFileInput("");
    try {
      if (userIdCSVFileInputRef.current) userIdCSVFileInputRef.current.value = "";
    } catch (_) {
      /* noop */
    }
    setCountry([]);
    setUserTargetType(pushUserTargetTypes.broadcast.value);
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!sendNotificationSuccess) return;
    handleCreateNotificationTaskReset();
  }, [sendNotificationSuccess, handleCreateNotificationTaskReset]);

  useEffect(() => {
    return () => clearTimeout(fetchNotificationTimeout);
  }, [fetchNotificationTimeout]);

  useEffect(() => {
    const expectedUserCount =
      lastNotificationDetails?.status?.taskProcessStatus?.userCount +
      lastNotificationDetails?.status?.taskProcessStatus?.userFailedCount;
    const userSentCount =
      lastNotificationDetails?.status?.notificationProcessStatus?.userSentCount;
    const failedUserCount =
      lastNotificationDetails?.status?.notificationProcessStatus?.failedUserSentCount +
      lastNotificationDetails?.status?.notificationProcessStatus?.tokenNotFoundUserCount +
      lastNotificationDetails?.status?.taskProcessStatus?.userFailedCount;

    if (lastNotificationDetails?.taskId) {
      clearTimeout(fetchNotificationTimeout);
      if (
        !(
          lastNotificationDetails?.status?.taskStatus === "processed" &&
          (expectedUserCount === userSentCount ||
            expectedUserCount === failedUserCount)
        )
      ) {
        setFetchNotificationTimeout(
          setTimeout(() => {
            getNotificationTask(
              lastNotificationDetails?.taskId,
              "updateSentNotification"
            );
          }, notificationDetailsRefreshRate)
        );
      }
    }

    if (
      lastNotificationDetails?.channel?.type !==
      notificationChannelTypes?.pushNotification?.value
    ) {
      return;
    }

    setNotificationTaskData(
      JSON.stringify(lastNotificationDetails) !== "{}"
        ? {
            expectedUserCount: (
              <div title="The expected number of users that will receive the notification.">
                {expectedUserCount}
              </div>
            ),
            fullTaskId: lastNotificationDetails?.taskId,
            taskId: (
              <div>
                <div title={lastNotificationDetails?.taskId}>
                  {"..." +
                    lastNotificationDetails?.taskId?.substring(
                      lastNotificationDetails?.taskId?.length - 6,
                      lastNotificationDetails?.taskId?.length
                    )}
                </div>
              </div>
            ),
            userCount: (
              <div title="The number of users that received the notification.">
                {userSentCount}
              </div>
            ),
            taskStatus: lastNotificationDetails?.status?.taskStatus,
            failureCount: (
              <div title="The number of users the notification failed to reach.">
                {failedUserCount}
              </div>
            ),
            notificationType:
              notificationChannelTypes[
                lastNotificationDetails?.channel?.type + "Notification"
              ]?.label,
            severity: lastNotificationDetails?.severity || "-",
            createdAt: format(
              new Date(lastNotificationDetails?.creationDate * 1000),
              "dd MMM yyyy 'AT' hh:mm a"
            ),
            expiryDate: lastNotificationDetails?.expiry
              ? format(
                  new Date(lastNotificationDetails?.expiry * 1000),
                  "dd MMM yyyy 'AT' hh:mm a"
                )
              : "-",
            payload: { ...lastNotificationDetails },
            msg: lastNotificationDetails?.message?.payload || {},
            action: (data) => (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "start",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <AppIconButton
                  variant="primary"
                  size="small"
                  icon={<VisibilityIcon fontSize="small" />}
                  tooltip="View notification"
                  onClick={() => {
                    setDialog(true);
                    setDialogTab(0);
                    setDialogContents({
                      content: "notificationDetails",
                      title: "Notification Details",
                      data,
                    });
                  }}
                />
              </Box>
            ),
          }
        : {}
    );
  }, [lastNotificationDetails]);

  const submitPushNotificationTask = (notificationTaskRequest) => {
    setNotificationTaskData({});
    createNotificationTask(notificationTaskRequest);
    window.scrollTo(0, 0);
  };

  function handleCreateNotificationTask() {
    if (!severity) {
      setAlert("Please select Severity", "danger");
      window.scrollTo(0, 0);
      return;
    }

    if (!expiryDate) {
      setAlert("Please select expiry date", "danger");
      window.scrollTo(0, 0);
      return;
    }

    if (!pnPlatforms.iosPlatform && !pnPlatforms.androidPlatform) {
      setAlert("Please select at least one platform", "danger");
      window.scrollTo(0, 0);
      return;
    }

    const messageError = validatePushMessageForm(pushMessageData);
    if (messageError) {
      setAlert(messageError, "danger");
      window.scrollTo(0, 0);
      return;
    }

    const platform = [];
    if (pnPlatforms.iosPlatform) platform.push("ios");
    if (pnPlatforms.androidPlatform) platform.push("an");

    const notificationTaskRequest = {
      channel: {
        type: notificationChannelTypes.pushNotification.value,
        platform,
      },
      message: {
        payload: buildPushMessagePayload(pushMessageData, expiryDate),
      },
      severity,
      users: {},
      expiry: getNotificationExpiryUnixSeconds(expiryDate),
    };

    if (userTargetType === pushUserTargetTypes.broadcast.value) {
      const selectedCountries = Array.isArray(country) ? country : [];
      if (!selectedCountries.length) {
        setAlert("Please select at least one country", "danger");
        window.scrollTo(0, 0);
        return;
      }
      if (
        !showBroadcastCountryAlert(selectedCountries)
      ) {
        return;
      }
      notificationTaskRequest.users.broadcast = true;
      notificationTaskRequest.users.country = selectedCountries.map((code) =>
        code.toLowerCase()
      );
      submitPushNotificationTask(notificationTaskRequest);
      return;
    }

    if (!uploadedUserIdFileInput) {
      setAlert("Please upload a CSV file with user IDs", "danger");
      window.scrollTo(0, 0);
      return;
    }
    if (!uploadedUserIdFileInput?.type?.includes("csv")) {
      setAlert("Please upload a csv file.", "danger");
      window.scrollTo(0, 0);
      return;
    }

    parseCsvUserIdsFromFile(uploadedUserIdFileInput)
      .then((userIds) => {
        if (!userIds?.length) {
          setAlert("Empty File of User Ids Uploaded", "danger");
          window.scrollTo(0, 0);
          return;
        }
        notificationTaskRequest.users.userIds = userIds;
        submitPushNotificationTask(notificationTaskRequest);
      })
      .catch(() => {
        setAlert("Failed to Parse User IDs Uploaded List", "danger");
      });
  }

  return (
    <Fragment>
      <Alert />
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          pb: 2,
        }}
      >
        {notificationTaskData && JSON.stringify(notificationTaskData) !== "{}" && (
          <Paper sx={{ ...lastSentSectionCardSx, mt: 1 }}>
            <SectionHeading sx={{ mb: 1 }}>Last Sent Notification details</SectionHeading>
            <DataTable
              columns={Object.keys(tableHeaders).map((key) => ({
                key,
                label: tableHeaders[key]?.label,
                align: tableHeaders[key]?.align,
                width: tableHeaders[key]?.width,
                minWidth: tableHeaders[key]?.minWidth,
                justifyContent: tableHeaders[key]?.style?.justifyContent,
                render: (val, row) => {
                  const value = row?.hasOwnProperty(key)
                    ? typeof row[key] === "function"
                      ? row[key](row)
                      : row[key]
                    : "-";
                  const isLong = String(value).length > 200;
                  if (key === "taskId") {
                    return (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <span
                          style={{
                            whiteSpace: isLong ? "normal" : "nowrap",
                            wordBreak: isLong ? "break-word" : "normal",
                          }}
                        >
                          {value}
                        </span>
                        <AppIconButton
                          size="small"
                          variant="ghost"
                          tooltip="Copy Task ID"
                          onClick={() => {
                            setCopied(true);
                            setTimeout(() => setCopied(false), 3000);
                            navigator?.clipboard?.writeText(row?.fullTaskId);
                          }}
                          icon={
                            copied ? <CheckCircleOutlineIcon /> : <FileCopyIcon />
                          }
                        />
                      </Box>
                    );
                  }
                  return (
                    <span
                      style={{
                        whiteSpace: isLong ? "normal" : "nowrap",
                        wordBreak: isLong ? "break-word" : "normal",
                      }}
                    >
                      {value}
                    </span>
                  );
                },
              }))}
              data={[notificationTaskData]}
              rowKey="taskId"
              maxHeight="360px"
              minWidth="100%"
            />
          </Paper>
        )}

        <UserSelection
          userTargetType={userTargetType}
          handleUserTargetTypeChange={handleUserTargetTypeChange}
          allowedCountries={allowedCountries}
          uploadedUserIdFileInput={uploadedUserIdFileInput}
          handleUserIdFileInput={setUploadedUserIdFileInput}
          userIdCSVFileInputRef={userIdCSVFileInputRef}
          countries={country}
          handleUserCountries={setCountry}
        />

        <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2 }}>
         Configuration
        </Typography>
        <Paper sx={emailSectionCardSx}>
          <Box sx={configGridSx}>
            <Box sx={configFieldFullSx}>
              <Typography variant="body2" sx={{ fontWeight: 400 }}>
                Platforms
                <Typography component="span" color="error.main" sx={{ ml: 0.25 }}>
                  *
                </Typography>
              </Typography>
              <FormControl component="fieldset" sx={{ width: "100%" }}>
                <FormGroup
                  row
                  sx={{
                    gap: { xs: 0.5, sm: 2 },
                    flexWrap: "wrap",
                    alignItems: "center",
                    minHeight: 40,
                  }}
                >
                  <AppCheckbox
                    checked={pnPlatforms.androidPlatform}
                    onChange={(e) =>
                      handlePlatformChange(
                        notificationConstants.androidPlatform,
                        e.target.checked
                      )
                    }
                    name={notificationConstants.androidPlatform}
                    color="default"
                    label="Android"
                    sx={{ ml: "-11px" }}
                  />
                  <AppCheckbox
                    checked={pnPlatforms.iosPlatform}
                    onChange={(e) =>
                      handlePlatformChange(
                        notificationConstants.iosPlatform,
                        e.target.checked
                      )
                    }
                    name={notificationConstants.iosPlatform}
                    color="default"
                    label="iOS"
                    sx={{ ml: "-11px" }}
                  />
                </FormGroup>
              </FormControl>
            </Box>

            <Box sx={configFieldSx}>
              <Typography variant="body2" sx={{ fontWeight: 400 }}>
                Expiry
                <Typography component="span" color="error.main" sx={{ ml: 0.25 }}>
                  *
                </Typography>
              </Typography>
              <Box sx={{ ...controlSx, ...focusOutlineSx }}>
                <MuiDatePicker
                  label="Expiry Date"
                  value={expiryDate ? new Date(expiryDate) : null}
                  minDate={new Date()}
                  onChange={(date) =>
                    setExpiryDate(date ? toNotificationExpiryEndOfDay(date) : null)
                  }
                />
              </Box>
            </Box>

            <Box sx={configFieldSx}>
              <Typography variant="body2" sx={{ fontWeight: 400 }}>
                Severity
                <Typography component="span" color="error.main" sx={{ ml: 0.25 }}>
                  *
                </Typography>
              </Typography>
              <Box sx={controlSx}>
                <FormInput
                  type="select"
                  size="small"
                  fullWidth
                  name="severity"
                  value={severity}
                  onChange={(e) => setSeverity(e.target.value)}
                  options={severityOptions}
                  displayEmpty
                  renderValue={(v) => getOptionLabel(severityOptions, v, "Select Severity")}
                  MenuProps={selectMenuProps}
                  sx={focusOutlineSx}
                />
              </Box>
            </Box>

            <Box sx={configFieldSx}>
              <Typography variant="body2" sx={{ fontWeight: 400 }}>
                Type
                <Typography component="span" color="error.main" sx={{ ml: 0.25 }}>
                  *
                </Typography>
              </Typography>
              <Box sx={controlSx}>
                <FormInput
                  type="select"
                  size="small"
                  fullWidth
                  name="type"
                  value={pushMessageData?.type || ""}
                  onChange={(e) => handlePushTypeChange(e.target.value)}
                  options={pushNotificationTypeOptions}
                  displayEmpty
                  renderValue={(v) =>
                    getOptionLabel(pushNotificationTypeOptions, v, "Select Type")
                  }
                  MenuProps={selectMenuProps}
                  sx={focusOutlineSx}
                />
              </Box>
            </Box>

            <Box sx={configFieldSx}>
              <Typography variant="body2" sx={{ fontWeight: 400 }}>
                Category
                <Typography component="span" color="error.main" sx={{ ml: 0.25 }}>
                  *
                </Typography>
              </Typography>
              <Box sx={controlSx}>
                <FormInput
                  type="select"
                  size="small"
                  fullWidth
                  name="category"
                  value={pushMessageData?.category || ""}
                  onChange={(e) => handlePushMessageData("category", e.target.value)}
                  options={categoryOptions}
                  displayEmpty
                  disabled={!pushMessageData?.type}
                  renderValue={(v) => {
                    if (!pushMessageData?.type) return "Select Type first";
                    return getOptionLabel(categoryOptions, v, "Select Category");
                  }}
                  MenuProps={selectMenuProps}
                  sx={focusOutlineSx}
                />
              </Box>
            </Box>
          </Box>
        </Paper>

        <PushNotification
          messageData={pushMessageData}
          handleMessageData={handlePushMessageData}
          onImageUploadError={handleImageUploadError}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            flexWrap: "wrap",
            mt: 2.5,
            pb: 1,
          }}
        >
          <AppButton
            variant="primary"
            size="medium"
            onClick={handleCreateNotificationTask}
            disabled={sendNotificationInProgress}
            loading={sendNotificationInProgress}
          >
            Create Notification Task
          </AppButton>
          <AppButton
            variant="ghost"
            size="medium"
            onClick={handleCreateNotificationTaskReset}
            disabled={sendNotificationInProgress}
          >
            Reset
          </AppButton>
        </Box>
      </Box>
    </Fragment>
  );
};

export default SendNotification;
